import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { authApi } from '../../api/axios';

function validatePassword(password, confirmPassword) {
  if (password.length < 8) return 'Минимальная длина пароля — 8 символов';
  if (!/[a-z]/.test(password)) return 'Добавьте хотя бы одну строчную букву';
  if (!/[A-Z]/.test(password)) return 'Добавьте хотя бы одну заглавную букву';
  if (!/[0-9]/.test(password)) return 'Добавьте хотя бы одну цифру';
  if (password !== confirmPassword) return 'Пароли не совпадают';
  return '';
}

function decodePathToken(raw) {
  const value = (raw || '').trim();
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export default function ResetPassword() {
  const { token: pathToken } = useParams();
  const token = useMemo(() => decodePathToken(pathToken), [pathToken]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!token) {
      setError('Ссылка сброса недействительна');
      return;
    }
    const passwordError = validatePassword(password, confirmPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    setLoading(true);
    try {
      const response = await authApi.post('/reset-password', {
        token,
        new_password: password,
      });
      setSuccess(response?.data?.message || 'Пароль успешно обновлен');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Не удалось обновить пароль';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Typography variant="h5" gutterBottom>
          Новый пароль
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Установите новый пароль для вашего аккаунта.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label="Новый пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            margin="normal"
          />
          <TextField
            fullWidth
            type="password"
            label="Повторите пароль"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            margin="normal"
          />
          <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ mt: 2 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Сохранить пароль'}
          </Button>
        </form>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" underline="hover">
            Вернуться ко входу
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
